<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" title="Downtime Breakdown">
        <template #sidebar>
            <SidebarKpi label="Total Downtime" :value="formatMinutes(totalIncluded)" />
            <SidebarKpi label="Bag Jam Time" :value="formatMinutes(totalBagJam)" />
        </template>
        <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="bar" />
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import SidebarKpi from './SidebarKpi.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
});

const componentKey = ref(0);
const hasData = computed(() => props.data?.labels?.length > 0);

const totalIncluded = computed(() => (props.data?.included ?? []).reduce((a, b) => a + b, 0));
const totalBagJam = computed(() => (props.data?.bagJam ?? []).reduce((a, b) => a + b, 0));

function formatMinutes(mins) {
    if (mins == null) return '—';
    const totalSeconds = Math.round(mins * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                label: 'Machine Downtime',
                data: props.data.included,
                backgroundColor: chartColors.red,
                borderRadius: 2,
                barPercentage: 0.7,
            },
            {
                label: 'Bag Jam',
                data: props.data.bagJam,
                backgroundColor: chartColors.amber,
                borderRadius: 2,
                barPercentage: 0.7,
            },
            {
                label: 'Excluded',
                data: props.data.excluded,
                backgroundColor: chartColors.grey,
                borderRadius: 2,
                barPercentage: 0.7,
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            stacked: true,
            grid: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45 },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            grid: { drawBorder: false },
            title: { display: true, text: 'Duration', font: { size: 11 } },
            ticks: { callback: (value) => formatMinutes(value) },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 8,
            },
        },
        tooltip: {
            ...lightTooltip,
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${formatMinutes(ctx.parsed.y)}`,
            },
        },
    },
}));
</script>
